import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ActivityGroupEditor from '../activity/create_activity/ActivityGroupEditor';
import { generateId, type ActivityFormData } from '../activity/create_activity/types';
import { contactService } from '../../services/contactService';
import { preOrderService } from '../../services/preOrderService';
import type { Contact } from '../../types/contact';
import type { PreOrder } from '../../types/catalog';
import type { SaveActivityGroupResponse } from '../../types/activity';

const errorMessage = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: unknown } }).response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

const getDraftKey = (preOrderId: number): string => `convert-preorder-state-${preOrderId}`;

// Google profile name is "First Last"; the owner can correct the split before the Contact is created
const splitClientName = (clientName?: string): { firstName: string; lastName: string } => {
  const parts = (clientName ?? '').trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
};

/**
 * Turns a pre-order into ONE Activity Group (one activity per item, the owner picks the type of each:
 * STOCK_OUT / DROPSHIPPING — a group may mix them), then links the group to the pre-order.
 */
export default function ConvertPreOrderPage() {
  const { id, preOrderId } = useParams<{ id: string; preOrderId: string }>();
  const navigate = useNavigate();
  const companyId = parseInt(id ?? '');
  const orderId = parseInt(preOrderId ?? '');
  const detailPath = `/companies/${companyId}/preorders/${orderId}`;

  const [preOrder, setPreOrder] = useState<PreOrder | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [contactResolved, setContactResolved] = useState(false);
  const [contactChoiceMade, setContactChoiceMade] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [creatingContact, setCreatingContact] = useState(false);
  const [phoneChoiceMade, setPhoneChoiceMade] = useState(false);
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [savedGroupId, setSavedGroupId] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const loaded = await preOrderService.getById(orderId);
        setPreOrder(loaded);

        const names = splitClientName(loaded.clientName);
        setFirstName(names.firstName);
        setLastName(names.lastName);

        if (loaded.clientEmail) {
          const existing = await contactService.getByEmail(companyId, loaded.clientEmail);
          setContact(existing);
          // A saved draft means the owner already got past this step (returned from a picker page)
          setContactChoiceMade(existing !== null || sessionStorage.getItem(getDraftKey(orderId)) !== null);
        }
        setContactResolved(true);
      } catch (e) {
        setError(errorMessage(e, 'Не вдалося завантажити передзамовлення'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [companyId, orderId]);

  const initialActivities = useMemo<ActivityFormData[]>(() => {
    if (!preOrder) return [];
    return preOrder.items.map((item) => ({
      feId: generateId(),
      productId: item.productId,
      amount: item.quantity,
      unitPrice: item.unitPrice,
      // Same lightweight display objects the edit page builds for existing activities
      selectedProduct: { id: item.productId, name: item.productName, code: item.productCode } as never,
      clientId: contact?.id,
      selectedClient: contact ?? undefined,
    }));
  }, [preOrder, contact]);

  const handleCreateContact = async () => {
    if (!preOrder) return;

    setCreatingContact(true);
    try {
      const created = await contactService.create({
        companyId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: preOrder.clientEmail,
        phoneNumber: preOrder.clientPhone || undefined,
        type: 'CLIENT',
      });
      setContact(created);
      setContactChoiceMade(true);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Не вдалося створити контакт'));
    } finally {
      setCreatingContact(false);
    }
  };

  const handleSkipContact = () => {
    setContactChoiceMade(true);
  };

  const handleUpdatePhone = async () => {
    if (!preOrder || !contact) return;

    setUpdatingPhone(true);
    try {
      const updated = await contactService.update(
        contact.id,
        {
          type: contact.type,
          lastName: contact.lastName,
          firstName: contact.firstName,
          middleName: contact.middleName,
          email: contact.email,
          organizationId: contact.organizationId,
          phoneNumber: preOrder.clientPhone,
        },
      );
      setContact(updated);
      setPhoneChoiceMade(true);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Не вдалося оновити номер контакту'));
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handleKeepContactPhone = () => {
    setPhoneChoiceMade(true);
  };

  const linkGroup = async (groupId: number) => {
    setLinking(true);
    try {
      await preOrderService.fulfill(orderId, [groupId]);
      sessionStorage.removeItem(getDraftKey(orderId));
      navigate(detailPath);
    } catch (e) {
      setError(errorMessage(e, 'Не вдалося прив’язати групу до передзамовлення'));
    } finally {
      setLinking(false);
    }
  };

  const handleGroupSaved = (response: SaveActivityGroupResponse) => {
    const groupId = response.activityGroup.data.id;
    setSavedGroupId(groupId);
    linkGroup(groupId);
  };

  const handleRetryLink = () => {
    if (savedGroupId !== null) linkGroup(savedGroupId);
  };

  const handleCancel = () => {
    sessionStorage.removeItem(getDraftKey(orderId));
    navigate(detailPath);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isActive = preOrder?.status === 'NEW' || preOrder?.status === 'CONFIRMED';

  // The order's phone is wiped from the server once the order is fulfilled, so the owner has to
  // decide now whether it replaces the number stored in the contact
  const phoneDiffers = contact !== null
    && !!preOrder?.clientPhone
    && contact.phoneNumber !== preOrder.clientPhone;
  const phoneDecisionPending = phoneDiffers && !phoneChoiceMade;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          size="small"
        >
          До передзамовлення
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Реалізація передзамовлення №{orderId}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preOrder && !isActive && (
        <Alert severity="warning">Реалізувати можна лише нове або підтверджене передзамовлення.</Alert>
      )}

      {savedGroupId !== null && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" disabled={linking} onClick={handleRetryLink}>
              Повторити
            </Button>
          }
        >
          Групу активностей №{savedGroupId} створено, але її ще не прив’язано до передзамовлення.
          Не створюйте активності повторно — натисніть «Повторити».
        </Alert>
      )}

      {preOrder && isActive && contactResolved && !contactChoiceMade && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Клієнта {preOrder.clientEmail} немає серед контактів
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Створіть контакт з цим email (він потрібен для активності) або оберіть наявний вручну
              у формі нижче.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Прізвище"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <TextField
                label="Ім’я"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={creatingContact || !firstName.trim() || !lastName.trim()}
                onClick={handleCreateContact}
              >
                Створити контакт
              </Button>
              <Button variant="outlined" disabled={creatingContact} onClick={handleSkipContact}>
                Обрати вручну
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {preOrder && isActive && contactChoiceMade && phoneDecisionPending && savedGroupId === null && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Номер у замовленні відрізняється від номера в контакті
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              У замовленні: {preOrder.clientPhone}. У контакті: {contact?.phoneNumber || 'номера немає'}.
              Після реалізації номер із замовлення буде видалено з сервера, тому оберіть, що зберегти.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={updatingPhone}
                onClick={handleUpdatePhone}
              >
                Оновити номер у контакті
              </Button>
              <Button
                color="inherit"
                disabled={updatingPhone}
                onClick={handleKeepContactPhone}
              >
                Залишити як є
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {preOrder && isActive && contactChoiceMade && !phoneDecisionPending && savedGroupId === null && (
        <ActivityGroupEditor
          companyId={companyId}
          storageKey={getDraftKey(orderId)}
          initialDescription={`Передзамовлення №${orderId}`}
          initialActivities={initialActivities}
          onSuccess={handleGroupSaved}
          onCancel={handleCancel}
        />
      )}
    </Box>
  );
}
